import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

type Props = {
  onMenuClick: () => void;
};

export default function AdminTopbar({ onMenuClick }: Props) {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const displayName = profile?.full_name || user?.email?.split("@")[0] || "Member";
  const initials =
    profile?.initials ||
    displayName
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  const jobTitle = profile?.job_title || "Portal Member";

  const onSignOut = async () => {
    setMenuOpen(false);
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <header className="h-16 bg-white/80 backdrop-blur border-b border-ink-100 px-4 md:px-6 flex items-center justify-between gap-4 sticky top-0 z-20">
      {/* Left: menu + search */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button
          onClick={onMenuClick}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-700 cursor-pointer"
        >
          <i className="ri-menu-line text-xl"></i>
        </button>

        <div className="relative w-full max-w-md">
          <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            placeholder="Search projects, customers, crew…"
            className="w-full text-sm pl-10 pr-4 py-2.5 rounded-md bg-cream-100 border border-transparent focus:bg-white focus:border-ink-200 outline-none transition-colors"
          />
          <span className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 text-[10px] text-ink-400 font-semibold">
            <kbd className="px-1.5 py-0.5 bg-white border border-ink-200 rounded">⌘</kbd>
            <kbd className="px-1.5 py-0.5 bg-white border border-ink-200 rounded">K</kbd>
          </span>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate("/projects?new=1")}
          className="hidden md:inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-add-line"></i>
          New Project
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className={`flex items-center gap-2.5 pl-1 pr-2.5 py-1 rounded-md hover:bg-cream-100 cursor-pointer ${
              menuOpen ? "bg-cream-100" : ""
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center text-xs font-bold">
              {initials}
            </div>
            <div className="hidden md:block text-left max-w-[140px]">
              <div className="text-xs font-semibold text-ink-900 leading-tight truncate">
                {displayName}
              </div>
              <div className="text-[10px] text-ink-500 leading-tight truncate">{jobTitle}</div>
            </div>
            <i
              className={`ri-arrow-down-s-line text-ink-400 transition-transform ${
                menuOpen ? "rotate-180" : ""
              }`}
            ></i>
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-ink-100 rounded-lg shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] py-2 z-30">
              <div className="px-4 py-3 border-b border-ink-100">
                <div className="text-sm font-semibold text-ink-900 truncate">{displayName}</div>
                <div className="text-[11px] text-ink-500 truncate">{user?.email}</div>
                <span className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-50 text-brand-700">
                  <i className="ri-shield-user-line"></i>
                  {profile?.role || "staff"}
                </span>
              </div>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/profile");
                }}
                className="w-full text-left px-4 py-2.5 text-sm text-ink-700 hover:bg-cream-50 flex items-center gap-2.5 cursor-pointer"
              >
                <i className="ri-user-line text-ink-400"></i>
                Your profile
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/profile");
                }}
                className="w-full text-left px-4 py-2.5 text-sm text-ink-700 hover:bg-cream-50 flex items-center gap-2.5 cursor-pointer"
              >
                <i className="ri-settings-3-line text-ink-400"></i>
                Account settings
              </button>
              <div className="my-1 border-t border-ink-100"></div>
              <button
                onClick={onSignOut}
                className="w-full text-left px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2.5 cursor-pointer"
              >
                <i className="ri-logout-box-r-line"></i>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}